import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import Heading from '@atlaskit/heading/heading';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import Image from '@atlaskit/image/image';
import Link from '@atlaskit/link/link';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import { openLoomMeetingRecordings } from '@atlassian/loom-meeting-recordings';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { requestLoomMeetingNudgeReminder } from '../../../reminder-event';
import { useLoomMeetingNudgeAnalytics } from '../../../analytics';
import AiNotesIcon from './assets/ai-notes.svg';
import { NotInterestedConfirmationFlag } from './NotInterestedConfirmationFlag';
import { ReminderConfirmationFlag } from './ReminderConfirmationFlag';

type MeetingNudgeBannerProps = {
	cloudId: string;
	handleOnDismissed: () => void;
};

type ConfirmationFlag = 'notInterested' | 'remindMe' | null;

const DATA_PRIVACY_URL = 'https://www.atlassian.com/legal/privacy-policy';

const getDismissClickEvent = (clickIdentifier: 'not-interested' | 'remind-me') => ({
	messageEventType: 'clicked' as const,
	clickedElement: {
		clickIdentifier,
		clickedElement: 'button' as const,
		buttonType: 'secondary' as const,
	},
	action: { actionType: 'dismiss' as const },
	additionalMetadataFromFrontend: {},
});

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '100%',
	},
	action: {
		alignItems: 'center',
		backgroundColor: 'transparent',
		display: 'inline-flex',
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
		color: token('color.link'),
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	aiNotesIcon: {
		height: '40px',
		width: '40px',
	},
	privacyLinkContainer: {
		marginTop: token('space.150'),
	},
});

export const MeetingNudgeBanner = ({
	cloudId,
	handleOnDismissed,
}: MeetingNudgeBannerProps): JSX.Element => {
	const intl = useIntl();
	const [confirmationFlag, setConfirmationFlag] = useState<ConfirmationFlag>(null);
	const [isWhyModalOpen, setIsWhyModalOpen] = useState(false);
	const hasFinishedConfirmationRef = useRef(false);
	const { messageAcknowledged, messageDismissed } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { fireCtaClicked, fireScreenSeen, fireFlagDismissed } =
		useLoomMeetingNudgeAnalytics('sidenav');

	useEffect(() => {
		if (confirmationFlag !== null) {
			fireScreenSeen(confirmationFlag);
		}
	}, [confirmationFlag, fireScreenSeen]);

	useEffect(() => {
		if (isWhyModalOpen) {
			fireScreenSeen('whyAmISeeingThis');
		}
	}, [fireScreenSeen, isWhyModalOpen]);

	const handleConnectCalendar = () => {
		fireCtaClicked('connectCalendar');
		void messageDismissed();
		openLoomMeetingRecordings({
			bridge: 'loom-meeting-nudge-sidenav',
			cloudId,
			interactionSource: 'confluence',
			loomSource: 'confluence-nudge-sidenav',
		});
		handleOnDismissed();
	};

	const handleRemindMe = () => {
		fireCtaClicked('remindMe');
		void messageInstanceClicked(getDismissClickEvent('remind-me'));
		void messageDismissed();
		requestLoomMeetingNudgeReminder();
		hasFinishedConfirmationRef.current = false;
		setConfirmationFlag('remindMe');
	};

	const handleNotInterested = () => {
		fireCtaClicked('notInterested');
		void messageInstanceClicked(getDismissClickEvent('not-interested'));
		hasFinishedConfirmationRef.current = false;
		setConfirmationFlag('notInterested');
	};

	const finishConfirmation = (commitDismissal: boolean): boolean => {
		if (hasFinishedConfirmationRef.current) {
			return false;
		}
		hasFinishedConfirmationRef.current = true;
		if (commitDismissal) {
			void messageDismissed();
		}
		setConfirmationFlag(null);
		handleOnDismissed();
		return true;
	};

	const handleReminderConfirmationDismissed = () => {
		if (finishConfirmation(false)) {
			fireFlagDismissed('flagRemindMe');
		}
	};

	const handleNotInterestedConfirmationDismissed = () => {
		if (finishConfirmation(true)) {
			fireFlagDismissed('flagNotInterested');
		}
	};

	const handleNotInterestedUndo = () => {
		if (finishConfirmation(false)) {
			fireCtaClicked('undo');
		}
	};

	const handleWhyAmISeeingThis = () => {
		fireCtaClicked('whyAmISeeingThis');
		void messageAcknowledged();
		setIsWhyModalOpen(true);
	};

	return (
		<>
			{confirmationFlag === null && (
				<Box xcss={styles.container}>
					<Stack space="space.100">
						<Inline alignBlock="start" spread="space-between">
							<Box xcss={styles.aiNotesIcon}>
								<Image
									src={AiNotesIcon}
									alt=""
									width="40"
									height="40"
									testId="loom-meeting-nudge-ai-notes-icon"
								/>
							</Box>
							<DropdownMenu
								trigger={({ triggerRef, onClick, ...triggerProps }) => (
									<IconButton
										{...triggerProps}
										appearance="subtle"
										icon={ShowMoreHorizontalIcon}
										label={intl.formatMessage({
											id: 'post-office.twc-loom-meeting-nudge-sidenav.more-actions',
											defaultMessage: 'More actions',
											description: 'Label for the Loom meeting nudge options menu',
										})}
										onClick={(event) => {
											fireCtaClicked('more');
											onClick?.(event);
										}}
										ref={triggerRef}
										spacing="compact"
									/>
								)}
							>
								<DropdownItemGroup>
									<DropdownItem onClick={handleNotInterested}>
										<FormattedMessage
											id="post-office.twc-loom-meeting-nudge-sidenav.not-interested"
											defaultMessage="Not interested"
											description="Action to permanently turn off Loom meeting recording suggestions"
										/>
									</DropdownItem>
									<DropdownItem onClick={handleWhyAmISeeingThis}>
										<FormattedMessage
											id="post-office.twc-loom-meeting-nudge-sidenav.why-am-i-seeing-this"
											defaultMessage="Why am I seeing this?"
											description="Action to explain why the Loom meeting nudge was shown"
										/>
									</DropdownItem>
								</DropdownItemGroup>
							</DropdownMenu>
						</Inline>

						<Heading color="color.text" size="xsmall">
							<FormattedMessage
								id="post-office.twc-loom-meeting-nudge-sidenav.heading"
								defaultMessage="Turn meetings into action"
								description="Heading for a side navigation banner promoting Loom meeting recordings"
							/>
						</Heading>
						<Text as="p" color="color.text.subtle">
							<FormattedMessage
								id="post-office.twc-loom-meeting-nudge-sidenav.body"
								defaultMessage="Loom captures action items in Confluence and Jira so you don’t have to."
								description="Body copy for a side navigation banner promoting Loom meeting recordings"
							/>
						</Text>
						<Inline alignBlock="center" space="space.100">
							<Pressable onClick={handleConnectCalendar} xcss={styles.action}>
								<Text as="p" color="color.link">
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.connect-calendar"
										defaultMessage="Connect calendar"
										description="Action to open Loom's calendar connection flow"
									/>
								</Text>
							</Pressable>
							<span aria-hidden="true">·</span>
							<Pressable onClick={handleRemindMe} xcss={styles.action}>
								<Text as="p" color="color.link">
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.remind-me"
										defaultMessage="Remind me"
										description="Action to defer the Loom calendar reminder until the next publish or live-page load"
									/>
								</Text>
							</Pressable>
						</Inline>
					</Stack>
				</Box>
			)}
			{confirmationFlag === 'remindMe' && (
				<ReminderConfirmationFlag onDismissed={handleReminderConfirmationDismissed} />
			)}
			{confirmationFlag === 'notInterested' && (
				<NotInterestedConfirmationFlag
					onDismissed={handleNotInterestedConfirmationDismissed}
					onUndo={handleNotInterestedUndo}
				/>
			)}
			<ModalTransition>
				{isWhyModalOpen && (
					<ModalDialog onClose={() => setIsWhyModalOpen(false)} width="large">
						<ModalHeader>
							<ModalTitle>
								<FormattedMessage
									id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.title"
									defaultMessage="Why am I seeing this?"
									description="Title for an explanation of why the Loom meeting nudge was shown"
								/>
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<Text as="p">
								<FormattedMessage
									id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.introduction.ai-non-final"
									defaultMessage="We think <strong>Loom</strong> will improve your team’s productivity. The recommendation is based on:"
									description="Introduction in the Loom meeting nudge explanation modal describing why Atlassian recommends Loom to the user"
									values={{
										strong: (chunks) => <Text weight="bold">{chunks}</Text>,
									}}
								/>
							</Text>
							<ul>
								<li>
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.teamwork-collection.ai-non-final"
										defaultMessage="your access to the Teamwork Collection (which includes Loom)"
										description="First reason in the Loom meeting nudge explanation modal, describing the user’s access to the Teamwork Collection"
									/>
								</li>
								<li>
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.atlassian-tools.ai-non-final"
										defaultMessage="the tools you use (Confluence and Jira)"
										description="Second reason in the Loom meeting nudge explanation modal, identifying the Atlassian tools the user already uses"
									/>
								</li>
								<li>
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.meeting-workflow.ai-non-final"
										defaultMessage="the way you manage meetings and documentation in your existing workflow"
										description="Third reason in the Loom meeting nudge explanation modal, describing how the recommendation relates to the user’s workflow"
									/>
								</li>
							</ul>
							<Box xcss={styles.privacyLinkContainer}>
								<Link href={DATA_PRIVACY_URL} target="_blank">
									<FormattedMessage
										id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.privacy-link.ai-non-final"
										defaultMessage="Learn more about your data privacy with Atlassian"
										description="Link in the Loom meeting nudge explanation modal that opens Atlassian’s privacy policy in a new browser tab"
									/>
								</Link>
							</Box>
						</ModalBody>
						<ModalFooter>
							<Button appearance="primary" onClick={() => setIsWhyModalOpen(false)}>
								<FormattedMessage
									id="post-office.twc-loom-meeting-nudge-sidenav.why-modal.close.ai-non-final"
									defaultMessage="OK"
									description="Confirmation button that closes the Loom meeting nudge explanation modal"
								/>
							</Button>
						</ModalFooter>
					</ModalDialog>
				)}
			</ModalTransition>
		</>
	);
};
